import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

const DEFAULT_BACKOFFICE_FUNCTIONS_BASE = 'https://us-central1-acctoo-backoffice.cloudfunctions.net';

/** App Firestore rules block client writes to service_readers; provision via backoffice Cloud Function + Admin SDK. */
async function provisionServiceReaderDocument(appUid, email) {
  const { backofficeAuth } = require('./backofficeFirebaseClient');
  const boUser = backofficeAuth.currentUser;
  if (!boUser) {
    throw new Error('Backoffice sign-in required to provision service_readers');
  }
  const token = await boUser.getIdToken();
  const base = process.env.REACT_APP_BACKOFFICE_FUNCTIONS_URL || DEFAULT_BACKOFFICE_FUNCTIONS_BASE;
  const res = await fetch(`${base}/provisionAppServiceReader`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid: appUid, email: email || null })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `provisionAppServiceReader failed: ${res.status}`);
  }
}

// Secondary client pointing to the main app's Firebase (acctoo-app)
const appProjectConfig = {
  apiKey: 'AIzaSyDrx0Sxs0yoJHUahGB59ojOEulPfNd57_Y',
  authDomain: 'acc-app-e5316.firebaseapp.com',
  projectId: 'acc-app-e5316',
  storageBucket: 'acc-app-e5316.firebasestorage.app'
};

let secondaryApp = null;
export const getAppFirestore = () => {
  if (!secondaryApp) {
    const existing = getApps().find((a) => a.name === 'acctoo-app');
    secondaryApp = existing || initializeApp(appProjectConfig, 'acctoo-app');
  }
  return getFirestore(secondaryApp);
};

// Export appDb as an alias for getAppFirestore for convenience
export const appDb = getAppFirestore();

export default getAppFirestore;

// Auth for the main app project
export const getAppAuth = () => {
  if (!secondaryApp) {
    const existing = getApps().find((a) => a.name === 'acctoo-app');
    secondaryApp = existing || initializeApp(appProjectConfig, 'acctoo-app');
  }
  return getAuth(secondaryApp);
};

// Deduplicate service sign-in across the whole app lifecycle
let serviceSignInPromise = null;
let cachedServiceUser = null;
let originalBackofficeUser = null;

// Store the original backoffice user before switching
const storeOriginalUser = () => {
  if (!originalBackofficeUser) {
    const { backofficeAuth } = require('./backofficeFirebaseClient');
    originalBackofficeUser = backofficeAuth.currentUser;
    console.log('💾 Stored original backoffice user:', originalBackofficeUser?.uid);
  }
};

// Restore the original backoffice user after app operations
const restoreOriginalUser = async () => {
  if (originalBackofficeUser) {
    console.log('🔄 Restoring original backoffice user:', originalBackofficeUser.uid);
    // Note: Firebase doesn't allow manual user restoration, but we can track it
    // The backoffice will handle re-authentication as needed
  }
};

// Ensure service authentication into the app project so rules allow backoffice reads
export const ensureAppServiceSignIn = async () => {
  console.log('🔐 ensureAppServiceSignIn called');
  
  // Store the current backoffice user before switching
  storeOriginalUser();
  
  if (cachedServiceUser) {
    console.log('✅ Using cached service user:', cachedServiceUser.uid);
    return cachedServiceUser;
  }
  
  if (serviceSignInPromise) {
    console.log('⏳ Waiting for existing sign-in promise...');
    return serviceSignInPromise;
  }

  const auth = getAppAuth();

  // Use the existing admin credentials that should already be verified
  const email = process.env.REACT_APP_APP_SERVICE_EMAIL;
  const password = process.env.REACT_APP_APP_SERVICE_PASSWORD;
  if (!email || !password) {
    throw new Error(
      'App service credentials are not configured. Set REACT_APP_APP_SERVICE_EMAIL and REACT_APP_APP_SERVICE_PASSWORD in the backoffice environment (do not commit real secrets to git).'
    );
  }

  console.log('🔑 Attempting sign-in with configured app service account');

  serviceSignInPromise = (async () => {
    try {
      try {
        console.log('📝 Signing in with service account...');
        const cred = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Sign-in successful, UID:', cred.user.uid);
        
        // Verify the user's email verification status
        console.log('📧 Email verified:', cred.user.emailVerified);
        
        console.log('📝 Provisioning service_readers via backoffice function...');
        await provisionServiceReaderDocument(cred.user.uid, cred.user.email);
        console.log('✅ Service readers document provisioned');
        
        return cred.user;
      } catch (e) {
        console.error('❌ Sign-in error:', e.code, e.message);
        
        // Handle throttling with one backoff retry
        if (String(e.code) === 'auth/too-many-requests') {
          console.log('⏳ Too many requests, retrying after delay...');
          await new Promise(r => setTimeout(r, 3500));
          const cred = await signInWithEmailAndPassword(auth, email, password);
          await provisionServiceReaderDocument(cred.user.uid, cred.user.email);
          return cred.user;
        }
        // If user doesn't exist, create it
        if (String(e.code) === 'auth/user-not-found') {
          console.log('👤 Service account not found, creating new one...');
          const cred = await createUserWithEmailAndPassword(auth, email, password);
          
          // Send email verification for the service account
          console.log('📧 Service account created, should verify email manually');
          
          await provisionServiceReaderDocument(cred.user.uid, cred.user.email);
          return cred.user;
        }
        throw e;
      }
    } catch (error) {
      console.error('❌ Authentication failed:', error);
      throw error;
    }
  })()
  .then((u) => { 
    console.log('✅ Authentication completed successfully');
    cachedServiceUser = u; 
    serviceSignInPromise = null; 
    return u; 
  })
  .catch((err) => { 
    console.error('❌ Authentication promise failed:', err);
    serviceSignInPromise = null; 
    throw err; 
  });

  return serviceSignInPromise;
};

// Function to perform app operations with proper authentication
export const withAppAuthentication = async (operation) => {
  const serviceUser = await ensureAppServiceSignIn();
  try {
    const result = await operation();
    return result;
  } finally {
    // Note: We don't restore the original user here as it would cause more conflicts
    // The backoffice will handle its own authentication when needed
  }
};

// Simple solution: Just ensure we're signed in as the service user for app operations
// and let the backoffice handle its own authentication separately
// Export service user UID for use in other modules
let serviceUserUid = null;

export const getServiceUserUid = () => serviceUserUid;

export const ensureAppAuthentication = async () => {
  try {
    const auth = getAppAuth();
    const currentUser = auth.currentUser;

    const serviceEmail = process.env.REACT_APP_APP_SERVICE_EMAIL;
    if (!serviceEmail) {
      throw new Error('REACT_APP_APP_SERVICE_EMAIL is not configured.');
    }
    
    // If we're already signed in as the service user, verify service_readers doc exists
    if (currentUser && currentUser.email === serviceEmail) {
      console.log('✅ Already authenticated as service user');
      serviceUserUid = currentUser.uid;

      const db = getAppFirestore();
      try {
        await provisionServiceReaderDocument(currentUser.uid, currentUser.email);
        console.log('✅ Service readers document provisioned');
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (docError) {
        console.warn('⚠️ Could not provision service_readers document:', docError.message);
        try {
          const docRef = doc(db, 'service_readers', currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            console.log('✅ Service readers document exists');
          } else {
            console.log('❌ Service readers document does not exist');
          }
        } catch (readError) {
          console.warn('⚠️ Could not read service_readers document:', readError.message);
        }
      }

      return currentUser;
    }
    
    // Otherwise, sign in as service user
    console.log('🔑 Signing in as service user for app operations');
    const email = process.env.REACT_APP_APP_SERVICE_EMAIL;
    const password = process.env.REACT_APP_APP_SERVICE_PASSWORD;
    if (!email || !password) {
      throw new Error(
        'App service credentials are not configured. Set REACT_APP_APP_SERVICE_EMAIL and REACT_APP_APP_SERVICE_PASSWORD.'
      );
    }

    const cred = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ Service user authenticated:', cred.user.uid);
    serviceUserUid = cred.user.uid; // Store the service user UID
    
    await provisionServiceReaderDocument(cred.user.uid, cred.user.email);
    console.log('✅ Service readers document provisioned');

    await new Promise((resolve) => setTimeout(resolve, 200));
    
    return cred.user;
  } catch (error) {
    console.error('❌ App authentication failed:', error);
    throw error;
  }
};


